import React from "react";
import { ArrowUpRight, Quote, ChevronDown } from "lucide-react";
import { leadership } from "../mock/pages";
import { SectionHeading } from "./SectionHeading";

function Portrait({ person, size = "lg" }) {
  const [src, setSrc] = React.useState(person.image);
  const dim = size === "lg" ? 210 : 168;

  if (!src) {
    return (
      <div
        className="grid shrink-0 place-items-center rounded-full"
        style={{
          height: dim,
          width: dim,
          background: "var(--teal-800)",
          border: "1px solid rgba(216,169,79,.4)",
        }}
      >
        <div className="text-center">
          <span className="font-display block text-[1.9rem]" style={{ color: "var(--gold-200)" }}>
            {person.initials}
          </span>
          <span className="mt-1 block text-[0.58rem] uppercase tracking-[0.14em]" style={{ color: "rgba(251,247,240,.5)" }}>
            Photo soon
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="shrink-0 overflow-hidden rounded-full"
      style={{ height: dim, width: dim, border: "3px solid #fff", boxShadow: "0 22px 44px -26px rgba(8,46,41,.55)" }}
    >
      <img
        src={src}
        alt={person.name}
        onError={() => setSrc(person.fallback && person.fallback !== src ? person.fallback : null)}
        className="h-full w-full object-cover object-top"
      />
    </div>
  );
}

function Connector() {
  return (
    <div className="flex flex-col items-center py-6" aria-hidden="true">
      <span className="h-10 w-[2px]" style={{ background: "linear-gradient(to bottom, rgba(192,138,46,.15), var(--gold-400))" }} />
      <ChevronDown size={16} style={{ color: "var(--gold)" }} />
    </div>
  );
}

function LeaderCard({ person, tier, featured = false, dark }) {
  return (
    <article
      data-reveal=""
      className={`relative flex w-full flex-col items-center gap-7 rounded-2xl p-8 text-center sm:p-9 ${
        featured ? "lg:flex-row lg:gap-10 lg:text-left" : ""
      }`}
      style={{
        background: dark ? "rgba(251,247,240,.05)" : "#fff",
        border: dark ? "1px solid rgba(235,214,168,.16)" : "1px solid rgba(11,59,54,.09)",
        transition: "box-shadow .35s ease, transform .35s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-6px)";
        e.currentTarget.style.boxShadow = "0 34px 70px -44px rgba(8,46,41,.5)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "none";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <span
        className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 rounded-full px-4 py-1.5 text-[0.62rem] font-bold uppercase tracking-[0.18em] lg:left-9 lg:translate-x-0"
        style={{ background: "var(--gold)", color: "#fff" }}
      >
        {tier}
      </span>

      <Portrait person={person} size={featured ? "lg" : "sm"} />

      <div className={featured ? "lg:flex-1" : ""}>
        <h3
          className="font-display text-[1.45rem] leading-snug"
          style={{ color: dark ? "#FBF7F0" : "var(--teal-900)" }}
        >
          {person.name}
        </h3>
        <p
          className="mt-1.5 text-[0.72rem] uppercase tracking-[0.14em]"
          style={{ color: dark ? "var(--gold-200)" : "var(--gold)" }}
        >
          {person.role}
        </p>

        <div className={`mt-5 flex gap-2 ${featured ? "" : "justify-center"}`}>
          <Quote size={14} style={{ color: "var(--gold)", flexShrink: 0, marginTop: 4 }} />
          <p
            className="font-display text-[1rem] italic leading-snug"
            style={{ color: dark ? "rgba(251,247,240,.88)" : "var(--teal-700)" }}
          >
            {person.quote}
          </p>
        </div>

        <p
          className="mt-4 text-[0.87rem] leading-relaxed"
          style={{ color: dark ? "rgba(251,247,240,.7)" : "var(--muted-ink)" }}
        >
          {person.bio}
        </p>

        {person.link && (
          <a
            href={person.link}
            target="_blank"
            rel="noreferrer"
            className="group mt-5 inline-flex items-center gap-2 text-[0.74rem] font-bold uppercase tracking-[0.1em]"
            style={{ color: "var(--gold)" }}
          >
            Full profile
            <ArrowUpRight
              size={14}
              className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              style={{ transition: "transform .3s ease" }}
            />
          </a>
        )}
      </div>
    </article>
  );
}

export default function Leadership({ tone = "light" }) {
  const dark = tone === "dark";
  const [chairman, secretary, ceo, principal] = leadership;

  return (
    <section
      id="leadership"
      className="relative py-24 lg:py-32"
      style={{ background: dark ? "var(--teal-900)" : "var(--cream)" }}
    >
      {dark && <div className="grain absolute inset-0" />}

      <div className="relative mx-auto max-w-[1280px] px-5 lg:px-6">
        <SectionHeading
          eyebrow="Leadership"
          title="The people behind the Academy"
          sub="Five decades of institution building, brought to a school campus — with a leadership line that treats every admission as a long-term promise to a family."
          tone={dark ? "light" : "dark"}
          align="center"
        />

        <div className="mx-auto mt-20 max-w-[980px]">
          <LeaderCard person={chairman} tier="Chairman" featured dark={dark} />
          <Connector />

          <div className="mx-auto max-w-[760px]">
            <LeaderCard person={secretary} tier="Secretary" featured dark={dark} />
          </div>
          <Connector />

          <div className="mx-auto max-w-[760px]">
            <LeaderCard person={ceo} tier="Chief Executive Officer" featured dark={dark} />
          </div>
          <Connector />

          <div className="mx-auto max-w-[760px]">
            <LeaderCard person={principal} tier="Principal" featured dark={dark} />
          </div>
        </div>
      </div>
    </section>
  );
}
